import * as Font from "expo-font";
import * as SplashScreen from "expo-splash-screen";
import { useCallback, useEffect, useRef, useState } from "react";
import { AppState, AppStateStatus } from "react-native";

import { useInit } from "../utils";

SplashScreen.preventAutoHideAsync();

type FontType = string | Record<string, Font.FontSource>;

function loadFonts(fonts: FontType[]) {
  return Promise.all(fonts.map((font) => Font.loadAsync(font)));
}

export function useAppLoading(fonts: FontType[] = []) {
  // State
  const [isLoading, setLoading] = useState(true);

  // Vars
  const isForeground = useRef(true);
  const isHidden = useRef(false);
  const isHiding = useRef(false);

  // Handlers
  const hideSplashScreen = useCallback(async () => {
    isHidden.current = true;

    await SplashScreen.hideAsync();
  }, []);

  const maybeHideSplashScreen = useCallback(async () => {
    isHiding.current = true;

    if (isForeground.current) {
      await hideSplashScreen();
    }
  }, [hideSplashScreen]);

  // Effects
  useInit(() => {
    async function load() {
      try {
        await loadFonts(fonts);
      } catch (err) {
        console.warn(err);
      } finally {
        setLoading(false);
      }
    }

    load();
  });

  useEffect(() => {
    const subscription = AppState.addEventListener(
      "change",
      async (status: AppStateStatus) => {
        isForeground.current = status === "active";

        if (isForeground.current && isHiding.current && !isHidden.current) {
          await hideSplashScreen();
        }
      }
    );

    return () => {
      subscription.remove();
    };
  }, [hideSplashScreen]);

  return {
    isLoading,
    hideSplashScreen: maybeHideSplashScreen,
  };
}
